import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  Badge,
  Modal,
  NumberInput,
  Select,
  Pagination,
  Loader,
  Center,
  Text,
  Anchor,
  ThemeIcon,
  Card,
} from "@mantine/core";
import { IconPlus, IconDownload, IconDatabase } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";
import { formatDate, formatFileSize } from "@/lib/format";

export default function DatasetList() {
  const [datasets, setDatasets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [type, setType] = useState("plate");
  const [trainCount, setTrainCount] = useState<number>(800);
  const [validationCount, setValidationCount] = useState<number>(200);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.dataset.list({ page, limit: 20 });
      setDatasets(res.data);
      setTotalPages(res.pagination.totalPages);
      setTotal(res.pagination.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.dataset.create({ type, trainCount, validationCount });
      notifications.show({
        title: "Амжилттай",
        message: "Датасет бэлдэж эхэллээ",
        color: "blue",
      });
      setModalOpen(false);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setCreating(false);
    }
  };

  const statusColor = (s: string) => (s === "done" ? "green" : "yellow");

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>Датасет</Title>
          <Badge variant="light" size="lg">{total}</Badge>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setModalOpen(true)} size="sm">
          Датасет үүсгэх
        </Button>
      </Group>

      {loading ? (
        <Center h={300}><Loader /></Center>
      ) : datasets.length === 0 ? (
        <Center h={300}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl">
              <IconDatabase size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">Датасет байхгүй</Text>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={800}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--app-table-header)' }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төрөл</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Сургалт</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Баталгаажуулалт</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нийт</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хэмжээ</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төлөв</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Татах</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {datasets.map((ds, idx) => (
                  <Table.Tr key={ds._id}>
                    <Table.Td c="dimmed" fz="sm">{(page - 1) * 20 + idx + 1}</Table.Td>
                    <Table.Td><Text size="sm" truncate maw={180}>{ds.name}</Text></Table.Td>
                    <Table.Td><Badge variant="light" size="sm">{ds.type}</Badge></Table.Td>
                    <Table.Td><Text size="sm">{ds.trainCount}</Text></Table.Td>
                    <Table.Td><Text size="sm">{ds.validationCount}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={600}>{ds.count}</Text></Table.Td>
                    <Table.Td><Text size="sm">{formatFileSize(ds.filesize)}</Text></Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(ds.datasetStatus)} size="sm">{ds.datasetStatus}</Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDate(ds.createdAt)}</Text></Table.Td>
                    <Table.Td>
                      {ds.datasetStatus === "done" && ds.filepath && (
                        <Anchor href={`/uploads/${ds.filepath}`} download>
                          <IconDownload size={16} />
                        </Anchor>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="center" p="md" style={{ borderTop: '1px solid var(--app-border)' }}>
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            </Group>
          )}
        </Card>
      )}

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={<Text fw={600}>Датасет үүсгэх</Text>} size="sm" centered>
        <Stack>
          <Select
            label="Төрөл"
            size="sm"
            data={[
              { value: "plate", label: "Улсын дугаар (PLATE)" },
              { value: "character", label: "Тэмдэгт (CHARACTER)" },
            ]}
            value={type}
            onChange={(v) => setType(v || "plate")}
          />
          <NumberInput label="Сургалтын зургийн тоо" value={trainCount} onChange={(v) => setTrainCount(Number(v) || 800)} min={1} size="sm" />
          <NumberInput label="Баталгаажуулалтын зургийн тоо" value={validationCount} onChange={(v) => setValidationCount(Number(v) || 200)} min={1} size="sm" />
          <Text size="sm" c="dimmed">Нийт: {trainCount + validationCount} зураг</Text>
          <Button onClick={handleCreate} loading={creating} size="sm">Эхлүүлэх</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
